<template>
  <div>
    <div style="margin-bottom: 20px;">
      <app-button
        :label="t('Adicionar Matriz Serviço-Prioridade')"
        icon="pi pi-plus"
        size="small"
        data-testid="so-service-priorities-add-btn"
        @click="handleNew"
      />
    </div>

    <!-- SOServicePriorities Table -->
    <s-o-service-priorities-table
      ref="soServicePrioritiesTableRef"
      @edit="handleEdit"
      @delete="handleDelete">
    </s-o-service-priorities-table>

    <!-- SOServicePrioritiesModal is centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import SOServicePrioritiesTable from './SOServicePrioritiesTable.vue';
// SOServicePrioritiesModal is centralized in ModalManager.vue
import { log } from '../../utils/DebugLogger';

type Id = number | string;

interface Props {
  translations?: Record<string, unknown>;
}

const props = withDefaults(defineProps<Props>(), {
  translations: () => ({})
});

interface DataTableInstance {
  loadData: () => void;
}

// Get i18n composable
const { t } = useI18n();

// Reactive data
const soServicePrioritiesTableRef = ref<DataTableInstance | null>(null);

// Methods
const loadData = async (): Promise<void> => {
  log('SOServicePrioritiesPage', 'loadData', 'Reloading data');
  if (soServicePrioritiesTableRef.value) {
    await soServicePrioritiesTableRef.value.loadData();
  }
};

const handleNew = (): void => {
  log('SOServicePrioritiesPage', 'handleNew', 'Opening modal for new service-priority via eventBus');
  eventBus.emit('modal.so-service-priorities.open', { action: 'create' });
};

const handleEdit = (id: Id): void => {
  log('SOServicePrioritiesPage', 'handleEdit', 'Editing service-priority ID via eventBus', id);
  eventBus.emit('modal.so-service-priorities.open', { id, action: 'edit' });
};

const handleDelete = (id: Id, data: Record<string, unknown> | null = null): void => {
  log('SOServicePrioritiesPage', 'handleDelete', 'Deleting service-priority ID via eventBus', id);
  eventBus.emit('modal.so-service-priorities.open', { id, data, action: 'delete' });
};

// Backward-compatible eventBus listeners (typed to match EventCallback shape)
const onBusNew = (): void => handleNew();
const onBusEdit = (data?: Id): void => {
  if (data !== undefined) handleEdit(data);
};
const onBusDelete = (data?: Id): void => {
  if (data !== undefined) handleDelete(data);
};

// Lifecycle
onMounted(() => {
  log('SOServicePrioritiesPage', 'mounted', 'Component mounted');

  // Set up eventBus listeners for backward compatibility
  eventBus.on('so-service-priority.new', onBusNew);
  eventBus.on<Id>('so-service-priority.edit', onBusEdit);
  eventBus.on<Id>('so-service-priority.delete', onBusDelete);
});

onBeforeUnmount(() => {
  log('SOServicePrioritiesPage', 'beforeUnmount', 'Cleaning up event listeners');

  // Clean up eventBus listeners
  eventBus.off('so-service-priority.new', onBusNew);
  eventBus.off<Id>('so-service-priority.edit', onBusEdit);
  eventBus.off<Id>('so-service-priority.delete', onBusDelete);
});

// Expose methods for parent components
defineExpose({
  loadData,
  handleNew,
  handleEdit,
  handleDelete
});
</script>

<style scoped>
</style>
